import { Stack, Text, PrimaryButton, BasicButton, Link } from "@certn/cascadia";
import { Card, TextField, TextInputFormikField } from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { Schemas } from "../../../types";
import { useAdjudicationEdit } from "./hooks/useAdjudicationEdit";
import { css } from "@emotion/css";

interface AdjudicationDetailsProps {
  adjudicationData?: Schemas["AdjudicationRubricDetails"];
  isLoading: boolean;
  error: string | null;
  mutate: () => void;
}

export function AdjudicationDetails({
  adjudicationData,
  isLoading,
  error,
  mutate,
}: AdjudicationDetailsProps) {
  const { t } = useTranslation();
  const { isEditing, validationSchema, handleSave, handleCancel, handleEdit } =
    useAdjudicationEdit({ mutate });

  if (isLoading) {
    return (
      <Card padding="space-4">
        <Stack>
          <Stack gap="space-0" orientation="horizontal" justify="space-between">
            <Stack gap="space-2">
              <Skeleton width="100px" height="24px" />
              <Skeleton width="300px" height="20px" />
            </Stack>
            <Skeleton width="60px" height="32px" />
          </Stack>

          <Stack gap="space-2">
            <Skeleton width="120px" height="24px" />
            <Skeleton width="150px" height="20px" />
          </Stack>

          <Stack gap="space-2">
            <Skeleton width="80px" height="24px" />
            <Skeleton width="250px" height="20px" />
          </Stack>
        </Stack>
      </Card>
    );
  }

  if (error || !adjudicationData) {
    return (
      <Stack gap="space-4">
        <Card padding="space-4">
          <Text style="global/body-primary">
            {t("No adjudication data found")}
          </Text>
        </Card>
      </Stack>
    );
  }

  return (
    <Card padding="space-5">
      <Formik
        initialValues={{ name: adjudicationData.name || "" }}
        validationSchema={validationSchema}
        onSubmit={handleSave}
        enableReinitialize
      >
        {({ isSubmitting, resetForm }) => (
          <Form>
            <Stack>
              <Stack
                gap="space-0"
                orientation="horizontal"
                justify="space-between"
              >
                <Stack gap="space-0">
                  <Text style="global/heading-medium">{t("Rubric name")}</Text>
                  {isEditing ? (
                    <TextInputFormikField
                      isRequired
                      name="name"
                      label=""
                      placeholder={t("Enter rubric name")}
                      testID="rubric-name-input"
                      autoFocus
                    />
                  ) : (
                    <Stack justify="space-between" orientation="horizontal">
                      <Text style="global/body-primary" color="text-weak">
                        {adjudicationData.name}
                      </Text>
                    </Stack>
                  )}
                </Stack>
                {!isEditing && (
                  <BasicButton onPress={handleEdit}>{t("Edit")}</BasicButton>
                )}
              </Stack>

              <Stack gap="space-0">
                <Text style="global/heading-medium">{t("Date created")}</Text>
                {isEditing ? (
                  <TextField
                    testID="date-created-input"
                    isDisabled
                    value={new Date(
                      adjudicationData.created
                    ).toLocaleDateString()}
                  />
                ) : (
                  <Text style="global/body-primary" color="text-weak">
                    {new Date(adjudicationData.created).toLocaleDateString()}
                  </Text>
                )}
              </Stack>

              <Stack gap="space-0">
                <Text style="global/heading-medium">
                  {t("Packages")} ({adjudicationData.packages.length})
                </Text>
                {isEditing ? (
                  <TextField
                    testID="usage-input"
                    isDisabled
                    value={adjudicationData.packages.length + " packages"}
                  />
                ) : (
                  <>
                    {adjudicationData.packages.length > 0 && (
                      <ul className={styles.list}>
                        {adjudicationData.packages.map(({ id, name }) => (
                          <li key={id}>
                            <Link
                              href={`/package/${id}`}
                              testID={`package-link-${id}`}
                              textColor="text-weak"
                            >
                              {name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </Stack>
            </Stack>
            <br />
            {isEditing && (
              <Stack orientation="horizontal" gap="space-3" justify="end">
                <BasicButton
                  onPress={() => {
                    resetForm();
                    handleCancel();
                  }}
                  isDisabled={isSubmitting}
                >
                  {t("Cancel")}
                </BasicButton>
                <PrimaryButton
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  {t("Save changes")}
                </PrimaryButton>
              </Stack>
            )}
          </Form>
        )}
      </Formik>
    </Card>
  );
}

const styles = {
  list: css({
    listStyle: "none",
    padding: 0,
    margin: 0,
  }),
};
